// lib/memberNotes.js
// 회원 특이사항·메모/연락 기록·신규회원 목록 API와 화면 표시 규칙
// (docs/architecture/admin-member-management-design.md 2·3·4·5절)
import { api, apiJson } from "./api";

// 연락 결과 (member_notes.contact_result)
export const CONTACT_RESULTS = [
  { value: "call", label: "통화" },
  { value: "no_answer", label: "부재" },
  { value: "sms", label: "문자" },
];

export function contactResultLabel(result) {
  const found = CONTACT_RESULTS.find((r) => r.value === result);
  return found ? found.label : "";
}

// 부재는 다시 연락해야 하므로 주의 색, 통화·문자는 연락됨
export function contactResultTone(result) {
  if (result === "no_answer") return "warning";
  if (result === "call" || result === "sms") return "safe";
  return "";
}

// 신규회원 연락 상태 (GET /v1/admin/new-members의 contact.status)
export const CONTACT_STATUSES = {
  none: { label: "연락 안 함", tone: "danger" },
  retry: { label: "부재 · 다시 필요", tone: "warning" },
  done: { label: "연락함", tone: "safe" },
};

export function contactStatusLabel(status) {
  return CONTACT_STATUSES[status]?.label || "";
}

export function contactStatusTone(status) {
  return CONTACT_STATUSES[status]?.tone || "";
}

// 수강권 상태 (memberships[].state)
export const MEMBERSHIP_STATES = {
  in_use: { label: "사용 중", tone: "safe" },
  upcoming: { label: "시작 예정", tone: "info" },
  expired: { label: "만료", tone: "expired" },
};

export function membershipStateLabel(state) {
  return MEMBERSHIP_STATES[state]?.label || "";
}

// 수강권 남은 일 수(만료일 − 오늘, KST 날짜) 문구. 회원관리 목록(MemberRow)과 회원 상세 수강권 카드가 같은 말을 쓴다
// (4차 리뷰: 같은 수강권이 목록은 "내일 만료"·"만료됨", 상세는 "1일 남음"·"만료"였다)
export function daysLeftText(days) {
  if (!Number.isFinite(days)) return "";
  if (days < 0) return MEMBERSHIP_STATES.expired.label;
  if (days === 0) return "오늘 만료";
  if (days === 1) return "내일 만료";
  return `${days}일 남음`;
}

// 수강권 남은 일 수 색: 만료 expired(회색), 7일 미만 danger(빨강), 14일 미만 warning(노랑), 그 밖 safe. 회원관리 목록의 만료일·"n일 남음"과
// 회원 상세 수강권 카드가 같이 쓴다. 7일 남음은 노랑이다: 서버의 "1주 미만" 필터(expired_at ≤ 지금+7일, 끝나는 날 23:59:59 저장)에도 들지
// 않는다(6차 리뷰: 목록 만료일만 ≤ 7로 빨강이라 한 칸에 빨간 날짜와 노란 "7일 남음"이 같이 보였다)
export function daysLeftTone(days) {
  if (!Number.isFinite(days)) return "";
  if (days < 0) return "expired";
  if (days < 7) return "danger";
  if (days < 14) return "warning";
  return "safe";
}

// 기간권은 total_count = -1: 주2회권은 "주 2회"(서버 회원 목록 remaining_classes와 같은 글자), 그 밖은 "무제한"
export function membershipCountText(m) {
  if (!m) return "";
  if (m.type === "주2회권") return "주 2회";
  if (m.total_count === -1) return "무제한";
  return `${m.remaining_count}/${m.total_count}회`;
}

// ---------------- 형식 ----------------

// "2026-10-14" 또는 "2026-10-14 15:20" → "2026.10.14"
export function dotDate(ymd) {
  if (!ymd || ymd === "-") return "";
  const [y, m, d] = String(ymd).slice(0, 10).split("-");
  if (!y || !m || !d) return String(ymd);
  return `${y}.${m}.${d}`;
}

// "2026-10-14" 또는 "2026-10-14 15:20" → "10.14"
export function shortDate(ymd) {
  if (!ymd || ymd === "-") return "";
  const [, m, d] = String(ymd).slice(0, 10).split("-");
  if (!m || !d) return String(ymd);
  return `${m}.${d}`;
}

// "2026-10-14 15:20" → "15:20"
export function timeOf(dateTime) {
  const s = String(dateTime || "");
  return s.length >= 16 ? s.slice(11, 16) : "";
}

// 메모·연락 기록 시각은 어디서나 한 형식(admin-web의 다른 날짜처럼 점):
// 시각은 "MM.DD HH:mm", 날짜만 쓸 때는 "MM.DD"

// "2026-10-14 15:20" → "10.14 15:20" (시각이 없으면 "10.14")
export function noteDateTime(dateTime) {
  const date = shortDate(dateTime);
  const time = timeOf(dateTime);
  return date && time ? `${date} ${time}` : date;
}

// "2026-10-14 15:20" 또는 "2026-10-14" → "10.14"
export function noteDate(dateTime) {
  return shortDate(dateTime);
}

export function prettyPhone(p) {
  if (!p) return "";
  const digits = String(p).replace(/\D/g, "");
  if (digits.length === 11)
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length === 10)
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return p;
}

// 쓰던 글을 버릴지 묻는다. 연락 기록 모달과 회원 상세(특이사항·기본 정보·메모·연락 폼)의 [취소], 그 화면을 떠날 때 모두 같은 문구다
export const DISCARD_DRAFT_MESSAGE = "작성 중인 내용이 있습니다. 닫을까요?";

export function confirmDiscardDraft() {
  return window.confirm(DISCARD_DRAFT_MESSAGE);
}

// ---------------- API ----------------

// 메모·연락 기록 (최신 순)
export async function fetchNotes(userId) {
  const res = await apiJson(
    `/v1/admin/members/${encodeURIComponent(userId)}/notes`
  );
  return res?.notes || [];
}

// note: { kind: "contact"|"memo", contact_result?, content }
export async function createNote(userId, note) {
  const body = { kind: note.kind, content: note.content || "" };
  if (note.kind === "contact") body.contact_result = note.contact_result;
  return apiJson(`/v1/admin/members/${encodeURIComponent(userId)}/notes`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// 보낸 칸만 바꾼다: { content?, contact_result? }
export async function updateNote(noteId, fields) {
  return apiJson(`/v1/admin/notes/${encodeURIComponent(noteId)}`, {
    method: "PUT",
    body: JSON.stringify(fields),
  });
}

// 204라 본문이 없다: apiJson 대신 상태만 확인
export async function deleteNote(noteId) {
  await api(`/v1/admin/notes/${encodeURIComponent(noteId)}`, {
    method: "DELETE",
  });
}

// 특이사항만 저장 (서버는 보낸 칸만 쓴다)
export async function saveAdminNote(userId, adminNote) {
  return apiJson(`/v1/admin/members/${encodeURIComponent(userId)}/profile`, {
    method: "PUT",
    body: JSON.stringify({ admin_note: adminNote }),
  });
}

// 신규회원 목록: { days, contact, search }
export async function fetchNewMembers({
  days = 30,
  contact = "all",
  search = "",
} = {}) {
  const params = new URLSearchParams({
    days: String(days),
    contact,
  });
  if (search && search.trim()) params.append("search", search.trim());
  return apiJson(`/v1/admin/new-members?${params.toString()}`);
}

// 회원관리 "최근 수정" 정렬(GET /v1/admin/members sort_by=recent_note)의 note_updated_at·note_updated_kind →
// "10.25 15:20 특이사항" / "10.24 09:00 메모". 다른 정렬(두 칸이 "")이나 모르는 값이면 ""
const NOTE_UPDATED_LABELS = { admin_note: "특이사항", memo: "메모" };
export function noteUpdatedText(member) {
  const at = member && member.note_updated_at;
  const label = member && NOTE_UPDATED_LABELS[member.note_updated_kind];
  if (!at || !label) return "";
  return `${noteDateTime(at)} ${label}`;
}
